import { ReactNode, ButtonHTMLAttributes, CSSProperties } from "react";

/* ----------------------------- Glass Card --------------------------------- */
export function GlassCard({
  children,
  className = "",
  style,
  organic = false,
  padding = 18,
}: {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  organic?: boolean;
  padding?: number | string;
}) {
  return (
    <div
      className={`${organic ? "mnf-organic" : "mnf-glass"} ${className}`}
      style={{ padding, ...style }}
    >
      {children}
    </div>
  );
}

/* ------------------------------- Pill Tag --------------------------------- */
export function PillTag({
  children,
  tone = "neutral",
}: {
  children: ReactNode;
  tone?: "neutral" | "forest" | "coral" | "purple" | "gold" | "fiber";
}) {
  const tones: Record<string, CSSProperties> = {
    neutral: { background: "rgba(30,74,53,0.06)", color: "var(--mnf-f)" },
    forest:  { background: "var(--mnf-f)", color: "#F5F0E8" },
    coral:   { background: "rgba(192,120,74,0.12)", color: "var(--mnf-t)" },
    purple:  { background: "var(--mnf-purpleL)", color: "var(--mnf-purple)" },
    gold:    { background: "var(--mnf-gold2)", color: "var(--mnf-gold)" },
    fiber:   { background: "var(--mnf-fiberBg)", color: "#2C6248", border: "1px solid var(--mnf-fiberBorder)" },
  };
  return (
    <span
      className="inline-flex items-center gap-1 mnf-body"
      style={{
        ...tones[tone],
        padding: "4px 10px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
      }}
    >
      {children}
    </span>
  );
}

/* --------------------------------- Button --------------------------------- */
type BtnVariant = "primary" | "secondary" | "ghost" | "coral";
interface BtnProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: BtnVariant;
  block?: boolean;
  size?: "sm" | "md" | "lg";
}
export function Btn({
  variant = "primary",
  block = false,
  size = "md",
  children,
  style,
  className = "",
  ...rest
}: BtnProps) {
  const heights = { sm: 36, md: 44, lg: 52 };
  const fontSizes = { sm: 12, md: 13, lg: 14 };
  const variants: Record<BtnVariant, CSSProperties> = {
    primary:   { background: "var(--mnf-f)",  color: "#F5F0E8", border: "1px solid var(--mnf-f)" },
    secondary: { background: "transparent",   color: "var(--mnf-f)", border: "1px solid rgba(30,74,53,0.18)" },
    ghost:     { background: "rgba(30,74,53,0.06)", color: "var(--mnf-f)", border: "1px solid transparent" },
    coral:     { background: "var(--mnf-t)",  color: "#fff", border: "1px solid var(--mnf-t)" },
  };
  return (
    <button
      {...rest}
      className={`mnf-body inline-flex items-center justify-center gap-2 transition-all hover-elevate active-elevate-2 ${className}`}
      style={{
        ...variants[variant],
        height: heights[size],
        padding: "0 18px",
        borderRadius: 999,
        fontSize: fontSizes[size],
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        width: block ? "100%" : undefined,
        cursor: "pointer",
        ...style,
      }}
    >
      {children}
    </button>
  );
}

/* ---------------------------------- Avatar -------------------------------- */
export function Avatar({
  initials,
  size = 36,
  src,
  ringColor,
}: {
  initials?: string;
  size?: number;
  src?: string;
  ringColor?: string;
}) {
  return (
    <div
      className="inline-flex items-center justify-center mnf-serif italic shrink-0"
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: src ? `center/cover no-repeat url(${src})` : "linear-gradient(135deg, #2C6248 0%, #1E4A35 100%)",
        color: "#F5F0E8",
        fontSize: size * 0.42,
        fontWeight: 500,
        boxShadow: ringColor
          ? `0 0 0 2px ${ringColor}, 0 0 0 4px rgba(245,240,232,0.9)`
          : "0 0 0 2px rgba(245,240,232,0.85)",
        flexShrink: 0,
      }}
    >
      {!src && initials}
    </div>
  );
}

/* --------------------------- Section title (eyebrow + serif) -------------- */
export function SectionTitle({
  eyebrow,
  title,
  trailing,
  className = "",
}: {
  eyebrow?: string;
  title: ReactNode;
  trailing?: ReactNode;
  className?: string;
}) {
  return (
    <div className={`flex items-end justify-between gap-3 ${className}`}>
      <div>
        {eyebrow && <p className="mnf-eyebrow mb-1">{eyebrow}</p>}
        <h2 className="mnf-serif" style={{ fontSize: 22, fontWeight: 500, color: "var(--mnf-fd)" }}>
          {title}
        </h2>
      </div>
      {trailing && <div className="text-xs mnf-body" style={{ color: "var(--mnf-tg)" }}>{trailing}</div>}
    </div>
  );
}
